import { collection, doc, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firestoreDb, firebaseAuth, firebaseFunctions } from './firebase'
import { callable } from './callable'
import type { StripboardDelta } from '../types/stripboard'
import type {
  MemberControls,
  ControlProfile,
} from '../types/memberControls'

export type MemberAutomationStarterAction = 'DAILY_SYNC' | 'END_OF_DAY_REPORT' | 'INVITE_MEMBER' | 'WORKSPACE_CHECK'
export type MemberAutomationStarterStatus = 'IDLE' | 'ACTIVE'

export type MemberAutomationStarterState = {
  id: string
  workspaceId: string
  actionType: MemberAutomationStarterAction
  label: string
  status: MemberAutomationStarterStatus
  lastRunId?: string | null
}

const starterCosts: Record<MemberAutomationStarterAction, number> = {
  DAILY_SYNC: 3,
  END_OF_DAY_REPORT: 5,
  INVITE_MEMBER: 0,
  WORKSPACE_CHECK: 2,
}

export const getMemberStarterCost = (actionType: MemberAutomationStarterAction) => starterCosts[actionType]

export const subscribeMemberAutomationStarters = (
  workspaceId: string,
  onChange: (states: MemberAutomationStarterState[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => onSnapshot(
  query(collection(firestoreDb, 'member_automation_starters'), where('workspaceId', '==', workspaceId)),
  (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as MemberAutomationStarterState)),
  (error) => onError?.(error),
)

export const executeMemberAutomationStarter = async (input: {
  workspaceId: string
  actionType: MemberAutomationStarterAction
  mode?: 'START' | 'STOP'
}) => {
  const callableFn = httpsCallable<typeof input, { actionType: MemberAutomationStarterAction; status: MemberAutomationStarterStatus; runId: string | null; creditsCharged: number }>(firebaseFunctions, 'executeMemberAutomationStarter')
  return (await callableFn(input)).data
}

export const createAIStatusProposal = async (input: {
  workspaceId: string
  title: string
  delta: StripboardDelta
  creditCost?: number
}) => {
  const callableFn = httpsCallable<Record<string, unknown>, { proposalId: string; status: 'PENDING'; creditCost: number }>(firebaseFunctions, 'createAIProposal')
  return (await callableFn({
    workspaceId: input.workspaceId,
    proposalType: 'STATUS_DELTA',
    title: input.title,
    delta: input.delta,
    creditCost: input.creditCost ?? 25,
  })).data
}

export const executeMemberAIProposal = async (proposalId: string) => {
  const callableFn = httpsCallable<{ proposalId: string }, { proposalId: string; status: 'EXECUTED'; creditsCharged: number; result: unknown }>(firebaseFunctions, 'executeAIProposal')
  return (await callableFn({ proposalId })).data
}

export interface SetMemberControlsInput {
  workspaceId: string
  targetUserId: string
  controls?: {
    profileId?: string | null
    overrides?: MemberControls | null
  } | null
}

export interface SetMemberControlsResult {
  ok: boolean
  effectiveControls: MemberControls | null
}

export interface UpsertControlProfileInput {
  workspaceId: string
  profileId?: string
  name: string
  controls: MemberControls
}

export interface UpsertControlProfileResult {
  ok: boolean
  profileId: string
  updated: number
  failed: number
}

export interface DeleteControlProfileInput {
  workspaceId: string
  profileId: string
}

export interface DeleteControlProfileResult {
  ok: boolean
  updated: number
  failed: number
}

export interface ApplyControlProfileInput {
  workspaceId: string
  profileId: string | null
  targetUserIds: string[]
}

export interface ApplyControlProfileResult {
  ok: boolean
  updated: number
  skipped: number
  failed: number
}

/**
 * Set member controls (profileId and/or overrides) on a workspace member.
 * Callable handles TMP4 permission ladder and recomputes effectiveControls.
 */
export async function setMemberControls(
  input: SetMemberControlsInput,
): Promise<SetMemberControlsResult> {
  return callable<SetMemberControlsInput, SetMemberControlsResult>('setMemberControls', input)
}

/**
 * Create or rename/update a control profile in the workspace.
 * Automatically recomputes effectiveControls for all members bound to this profile.
 */
export async function upsertControlProfile(
  input: UpsertControlProfileInput,
): Promise<UpsertControlProfileResult> {
  return callable<UpsertControlProfileInput, UpsertControlProfileResult>('upsertControlProfile', input)
}

/**
 * Delete a control profile from the workspace.
 * Recomputes effectiveControls for affected members, falling back to their overrides.
 */
export async function deleteControlProfile(
  input: DeleteControlProfileInput,
): Promise<DeleteControlProfileResult> {
  return callable<DeleteControlProfileInput, DeleteControlProfileResult>('deleteControlProfile', input)
}

export interface SetMemberControlsBulkInput {
  workspaceId: string
  targetUserIds: string[]
  patch: Partial<MemberControls> | null
}

export interface SetMemberControlsBulkResult {
  ok: boolean
  updated: number
  skipped: number
  failed: number
}

/**
 * Bulk apply or unbind a control profile to up to 500 members.
 * Skips workspace owners (TMP4).
 */
export async function applyControlProfile(
  input: ApplyControlProfileInput,
): Promise<ApplyControlProfileResult> {
  return callable<ApplyControlProfileInput, ApplyControlProfileResult>('applyControlProfile', input)
}

/**
 * Bulk set member controls overrides on up to 500 members.
 * Merges patch into each person's overrides (null clears a key).
 * Skips owners, and ADMINs when caller is not an owner (TMP4).
 */
export async function setMemberControlsBulk(
  input: SetMemberControlsBulkInput,
): Promise<SetMemberControlsBulkResult> {
  return callable<SetMemberControlsBulkInput, SetMemberControlsBulkResult>('setMemberControlsBulk', input)
}

/**
 * Subscribes to real-time control profiles for a workspace.
 */
export function subscribeToControlProfiles(
  workspaceId: string,
  cb: (profiles: ControlProfile[]) => void,
): Unsubscribe {
  if (!workspaceId) {
    cb([])
    return () => {}
  }
  try {
    const profilesRef = collection(firestoreDb, 'workspaces', workspaceId, 'control_profiles')
    return onSnapshot(
      profilesRef,
      (snapshot) => {
        const profiles: ControlProfile[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data()
          return {
            id: docSnap.id,
            name: typeof data.name === 'string' ? data.name : '',
            controls: (data.controls as MemberControls) ?? {},
            updatedAt: typeof data.updatedAt?.toDate === 'function' ? data.updatedAt.toDate().toISOString() : String(data.updatedAt ?? ''),
            updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
          }
        })
        cb(profiles)
      },
      (error) => {
        console.warn(`[subscribeToControlProfiles] Error listening to control profiles for ${workspaceId}:`, error)
        cb([])
      },
    )
  } catch (error) {
    console.warn(`[subscribeToControlProfiles] Initialization error for ${workspaceId}:`, error)
    cb([])
    return () => {}
  }
}

/**
 * Subscribes to the authenticated user's effectiveControls on their membership mirror doc.
 */
export function subscribeToMyEffectiveControls(
  workspaceId: string,
  cb: (controls: MemberControls | null) => void,
  explicitUid?: string,
): Unsubscribe {
  let uid = explicitUid
  if (!uid) {
    try {
      uid = firebaseAuth.currentUser?.uid
    } catch {
      uid = undefined
    }
  }
  if (!uid || !workspaceId) {
    cb(null)
    return () => {}
  }
  try {
    const membershipRef = doc(firestoreDb, 'users', uid, 'workspaceMemberships', workspaceId)
    return onSnapshot(
      membershipRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          cb(null)
          return
        }
        const data = snapshot.data()
        cb((data?.effectiveControls as MemberControls | undefined) ?? null)
      },
      (error) => {
        console.warn(`[subscribeToMyEffectiveControls] Error listening to effectiveControls for ${workspaceId}:`, error)
        cb(null)
      },
    )
  } catch (error) {
    console.warn(`[subscribeToMyEffectiveControls] Initialization error for ${workspaceId}:`, error)
    cb(null)
    return () => {}
  }
}
